import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { URL, ADMIN } from "../../../../lib/lib.js";
import "./component.css";

const Component = ({tableHeading}) => {
  const [menus, setMenus] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [showDeleteText, setShowDeleteText] = useState("");
  const [userIdToDelete, setUserIdToDelete] = useState(null); // Store user id to delete

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(URL + ADMIN.menu_view, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page,
          limit,
          search,
          sortField,
          sortOrder,
        }),
        credentials: 'include'
      });
      const data = await res.json();
      setMenus(data.data || []);
      setTotal(data.pagination.totalData || 0); // Total records in DB
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, sortField, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Show delete confirmation modal
  const handleDeleteClick = (user) => {
    setUserIdToDelete(user._id);
    setShowDeleteText(user._id);
    setShowDeleteModal(true); // Show the modal when delete is clicked
  };


  // Handle the delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteText === showDeleteText) {
      try {
        const response = await fetch(URL + ADMIN.menu_delete, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: userIdToDelete }),
          credentials: "include"
        });

        if (response.status === 200) {
          setShowDeleteModal(false);
          fetchData();
          setDeleteText('');
          setShowDeleteText('');
        }
      } catch (error) {
        console.error("Delete error:", error);
        alert("Error deleting user");
      }
    } else {
      alert("Please type 'DELETE' to confirm the deletion.");
    }
  };

  // Cancel the delete operation
  const handleDeleteCancel = () => {
    setShowDeleteModal(false); // Close the modal
    setDeleteText(""); // Clear the input field
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(total / limit);

  // Disable next and prev buttons if necessary
  const disableNext = page >= totalPages;
  const disablePrev = page <= 1;

  return (
    <div className="padd-dash">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h3 className="head-dash mb-0">{tableHeading}</h3>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={fetchData} disabled={loading} >
            <i className={`ri-refresh-line ${loading ? "spin" : ""}`}></i> Refresh
          </button>
          <Link to="/admin/add-menu" className="btn btn-primary">+ Add Menu</Link>
        </div>
      </div>

      {/* Search Box */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <input
          type="text"
          placeholder="Search..."
          className="form-control search-input"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // Reset to first page on search change
          }}
          style={{ width: "200px" }}
        />
        <div>
          Items per page:
          <select
            className="form-select d-inline-block ms-2"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1); // Reset to first page when limit changes
            }}
            style={{ width: "80px" }}
          >
            {[5, 10, 20, 50].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="user-table table-responsive" style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}>
        <table className="table table-bordered dynamic-table" style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}>
          <thead>
            <tr>
              {/* <th>#</th> */}
              <th onClick={() => handleSort("_id")} ># {sortField === "_id" && (<i className={`ms-1 ri-arrow-${sortOrder === "asc" ? "up" : "down"}-line`}></i>)}</th>
              {/* <th style={{ minWidth: '100px', whiteSpace: 'nowrap', cursor: "pointer" }}  onClick={() => handleSort("_id")} >Menu ID {sortField === "_id" && (<i className={`ms-1 ri-arrow-${sortOrder === "asc" ? "up" : "down"}-line`}></i>)}</th> */}
              <th>Hotel Name</th>
              <th>Product Name</th>
              <th>Hotel Details</th>
              <th>Product Details</th>
              <th>Status</th>
              <th style={{ minWidth: '100px', whiteSpace: 'nowrap' }} >Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center">Loading...</td>
              </tr>
            ) : menus.length ? (
              menus.map((menu, index) => (
                <tr key={menu._id}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  {/* <td>{menu._id}</td> */}
                  <td>{menu.hotelId?.name || "N/A"}</td>
                  <td>{menu.productId?.name || "N/A"}</td>
                  <td>
                    <strong>Name:</strong> {menu.hotelId?.name || 'N/A'}<br />
                    <strong>Email:</strong> {menu.hotelId?.email || 'N/A'}<br />
                    <strong>Phone:</strong> {menu.hotelId?.phone || 'N/A'}<br />
                    <strong>City:</strong> {menu.hotelId?.address?.city || 'N/A'}<br />
                    <strong>State:</strong> {menu.hotelId?.address?.state || 'N/A'}<br />
                    <strong>Country:</strong> {menu.hotelId?.address?.country || 'N/A'}
                  </td>

                  {/* Product Details */}
                  <td>
                    <strong>Name:</strong> {menu.productId?.name || 'N/A'}<br />
                    <strong>Brand:</strong> {menu.productId?.brand || 'N/A'}<br />
                    <strong>MRP:</strong> ₹{menu.productId?.mrp}<br />
                    <strong>Price:</strong> ₹{menu.productId?.confirmPrice}<br />
                    <strong>Category:</strong> {menu.productId?.categoryId?.name || 'N/A'}<br />
                    <strong>Description:</strong> {menu.productId?.description || 'N/A'}<br />
                  </td>

                  <td>{menu.status === "true" ? "Active" : menu.status === "false" ? "Inactive" : "N/A"}</td>

                  <td>
                    <div className="action-icons">
                      <Link to={`/admin/edit-menu/${menu._id}`}>
                        <i className="ri-pencil-line edit-icon"></i>
                      </Link>
                      <i className="ri-delete-bin-line delete-icon" onClick={() => handleDeleteClick(menu)}></i>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center">No menus found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {total > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
          <div>
            <span>
              Showing {Math.min((page - 1) * limit + 1, total)} -{" "}
              {Math.min(page * limit, total)} of {total} items
            </span>
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${disablePrev ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPage(page - 1)}
                  disabled={disablePrev}
                >
                  &laquo;
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => (
                <li
                  key={i}
                  className={`page-item ${page === i + 1 ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${disableNext ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPage(page + 1)}
                  disabled={disableNext}
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Modal for Delete Confirmation */}
      {showDeleteModal && (
        <div className="modal fade show" tabIndex="-1" role="dialog" aria-labelledby="deleteModalLabel" aria-hidden="true">
          <div className="modal-dialog" role="document">
            <div className="modal-content">

              <div className="modal-body">
                <p>Please type <strong>"{showDeleteText}"</strong> to confirm the deletion.</p>
                <br />
                <input
                  type="text"
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  placeholder="Type DELETE"
                  className="form-control" // Bootstrap form-control for input field styling
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-danger" onClick={handleDeleteConfirm} disabled={deleteText !== showDeleteText} >
                  Confirm Delete
                </button>
                <button className="btn btn-secondary" onClick={handleDeleteCancel}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Component;
